import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import Accordion from '../../Accordion';

/**
 * Simulator methodology: intro copy plus collapsible sections using the shared
 * Tailwind Accordion (API-compatible with react-bootstrap Accordion, not Bootstrap itself).
 *
 * Owns accordion state locally so toggling does not re-render the heavy chart parent.
 */
const GenerationProfileMethodologyAccordion = forwardRef(function GenerationProfileMethodologyAccordion(
  _props,
  ref
) {
  const [activeKey, setActiveKey] = useState(null);

  const openSection = useCallback((eventKey, sectionId) => {
    setActiveKey(eventKey);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const yOffset = -20;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 150);
  }, []);

  useImperativeHandle(ref, () => ({
    openFirstAccordion: () => openSection('0', 'section-demand'),
    openSection,
  }), [openSection]);

  return (
    <div className="mt-6 md:mt-8 px-2 sm:px-4 lg:px-6">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-card hover:shadow-card-hover transition-shadow overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
          <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-700">
            📖 Demand and resource profiles
          </h2>
          <p className="text-sm md:text-base text-gray-500 mt-0.5 mb-0">
            Methodology and data sources behind the hourly simulator
          </p>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-5">
        {/* Overview Section - Always Visible */}
        <div className="border-l-4 border-blue-500 pl-3 md:pl-4 py-2 mb-4">
          <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-2">
            How Your 8760-Hour Energy Profiles Are Built
          </h3>
          <div className="text-xs lg:text-sm text-gray-700 space-y-2">
            <p>
              This simulator generates hour-by-hour electricity profiles for an entire year (8,760 hours)
              using real climate data and trusted global energy datasets. Each profile reflects realistic
              generation patterns based on weather conditions, technology characteristics, and operational constraints.
            </p>
            <p className="font-medium">
              Key Principle: We combine high-resolution weather data with historical generation patterns
              to create realistic, validated hourly profiles.
            </p>
          </div>
        </div>

        {/* Nested methodology sections (Tailwind Accordion) */}
        <Accordion activeKey={activeKey} onSelect={setActiveKey} className="mt-6 border-t border-gray-100 pt-4 md:pt-5">

          {/* Demand Profile */}
          <Accordion.Item eventKey="0" id="section-demand">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">📊 Electricity Demand Profile</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">ERA5 (ECMWF) | Climate reanalysis</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">GEGIS + synde | Demand modeling</span>
                </div>
                <div className="space-y-2">
                  <p><strong>Source:</strong> Pre-calculated hourly electricity demand profiles for 2030 (SSP scenarios)</p>
                  <div>
                    <p className="font-medium mb-1">About ERA5:</p>
                    <p>ERA5 is the fifth generation of the European Centre for Medium-Range Weather Forecasts
                    (ECMWF) atmospheric reanalysis dataset, providing hourly estimates of atmospheric, land,
                    and oceanic climate variables from 1940 to the present on a 31 km global grid. It combines
                    vast historical observations from in-situ and satellite sources with advanced modeling and
                    data assimilation systems to produce consistent global estimates.</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Profile Creation:</p>
                    <p>These demand profiles were generated by the Global-Energy GIS (GEGIS) open-source package,
                    which produces hourly demand time series using machine learning methods with temperature
                    profiles (ERA5), population, and GDP as predictors. The profiles are accessed via the synde
                    workflow management system.</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">What you get:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>8,760 hourly electricity demand values for 2030</li>
                      <li>Based on historical weather years (2011, 2013, 2018)</li>
                      <li>Climate-driven daily and seasonal patterns</li>
                      <li>Temperature-sensitive heating and cooling loads</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Solar Profile */}
          <Accordion.Item eventKey="1" id="section-solar">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">☀️ Solar PV Generation Profile</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">Atlite + ERA5 | Open-source renewable energy modeling</span>
                </div>
                <div className="space-y-2">
                  <p><strong>Source:</strong> Atlite Grid Cell Data (ERA5 Weather)</p>
                  <div>
                    <p className="font-medium mb-1">How it works:</p>
                    <p className="mb-2">Solar generation is calculated from actual weather patterns using a multi-step process:</p>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>
                        <strong>Grid Cell Selection</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>We identify optimal solar sites within your selected country</li>
                          <li>Grid cells (~50 km resolution) are ranked by solar potential</li>
                          <li>Your specified capacity is distributed across the best locations</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Weather-to-Power Conversion</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Hourly solar irradiance from ERA5 climate data</li>
                          <li>Standard PV panel characteristics (tilt, orientation)</li>
                          <li>Capacity factors calculated for each hour</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Profile Aggregation</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Weighted combination of selected grid cells</li>
                          <li>Final profile matches your target capacity</li>
                          <li>8,760 hourly generation values in MW</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Why Optimal Sites:</p>
                    <p>We select high-capacity-factor locations to represent realistic deployment scenarios.
                    This assumes solar is built where it performs best, not uniformly distributed.</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">What You See:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Strong daily cycle (zero at night)</li>
                      <li>Seasonal variation (higher in summer)</li>
                      <li>Weather-driven variability (clouds reduce output)</li>
                      <li>Annual capacity factor typically 15-25%</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                    <p className="font-medium text-blue-900 mb-1">💡 Clever Approach:</p>
                    <p className="text-blue-800 text-xs">Instead of using country-average capacity factors, we select
                    optimal grid cells first, then generate profiles. This reflects how solar projects
                    are actually sited in reality.</p>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Wind Profile */}
          <Accordion.Item eventKey="2" id="section-wind">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">💨 Wind Generation Profile</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">Atlite + ERA5 | Wind-to-power modeling</span>
                </div>
                <div className="space-y-2">
                  <p><strong>Source:</strong> Atlite Grid Cell Data (ERA5 Weather)</p>
                  <div>
                    <p className="font-medium mb-1">How it works:</p>
                    <p className="mb-2">Wind generation follows the same grid-cell optimization approach as solar:</p>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>
                        <strong>Grid Cell Selection</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Optimal wind sites identified by capacity factor</li>
                          <li>Onshore and offshore wind treated separately</li>
                          <li>Capacity distributed across windiest locations</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Weather-to-Power Conversion (handled by Atlite)</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Hourly wind speeds from ERA5 climate data</li>
                          <li>Standard turbine power curves applied</li>
                          <li>Accounts for cut-in, rated, and cut-out wind speeds</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Profile Aggregation</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Combined output from all selected grid cells</li>
                          <li>Weighted by capacity allocation</li>
                          <li>8,760 hourly generation values in MW</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Key Insight:</p>
                    <p>Wind&apos;s variability is different from solar. While solar is predictable daily,
                    wind can change rapidly and unexpectedly, creating different grid integration challenges.</p>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Nuclear Profile */}
          <Accordion.Item eventKey="3" id="section-nuclear">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">⚛️ Nuclear Generation Profile</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">EMBER | Global electricity data</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">IRENA | International Renewable Energy Agency</span>
                </div>
                <div className="space-y-2">
                  <p><strong>Source:</strong> EMBER Monthly Generation Data + IRENA Capacity</p>
                  <div>
                    <p className="font-medium mb-1">How it works:</p>
                    <p className="mb-2">Nuclear operates as baseload power - running continuously at steady output:</p>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>
                        <strong>Monthly Generation</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Historical monthly generation from EMBER</li>
                          <li>Reflects typical nuclear operation patterns</li>
                          <li>Accounts for refueling and maintenance in monthly data</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Baseload Distribution</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Monthly generation evenly distributed across all hours</li>
                          <li>Creates flat profile (constant output)</li>
                          <li>Represents nuclear&apos;s role as reliable baseload</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Capacity Scaling</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Your capacity / Historical capacity = Scale Factor</li>
                          <li>Monthly generation scaled proportionally</li>
                          <li>Maintains baseload character</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Why Flat:</p>
                    <p>Nuclear plants run at steady output (80-95% capacity) with minimal variation.
                    This reflects their economic and technical design as baseload providers.</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">What You See:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Flat hourly profile (nearly constant)</li>
                      <li>Slight month-to-month variation (maintenance, refueling)</li>
                      <li>High capacity factor (85%+)</li>
                      <li>Foundation of reliable generation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Residual Load */}
          <Accordion.Item eventKey="4" id="section-residual">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">🧮 Residual Load (The Gap to Fill)</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="space-y-2">
                  <p className="font-medium">Key Concept: After Nuclear, Solar, and Wind Generate, What&apos;s Left?</p>
                  <p>The residual load is what remains to be served after baseload nuclear and variable
                  renewables have contributed. This is what flexible generation (like hydro) must fill.</p>
                  <div>
                    <p className="font-medium mb-1">Calculation:</p>
                    <p className="font-mono bg-gray-100 p-2 rounded">Residual = Demand - Nuclear - Solar - WindOn - WindOff</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">For each of the 8,760 hours:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Positive residual:</strong> More generation needed → Flexible sources dispatch</li>
                      <li><strong>Negative residual:</strong> Excess generation → Curtailment or storage needed</li>
                      <li><strong>Zero residual:</strong> Perfect balance (rare!)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Why This Matters:</p>
                    <p>The residual load determines:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>How much flexible generation capacity you need</li>
                      <li>When that capacity must operate</li>
                      <li>How variable renewables impact system flexibility</li>
                      <li>Whether you have curtailment or unmet demand</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Dispatch Priority:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Nuclear (baseload, always on)</li>
                      <li>Solar (when sun shines)</li>
                      <li>Wind (when wind blows)</li>
                      <li>Hydro (fills the gap)</li>
                    </ol>
                    <p className="mt-2">This ordering reflects real power system operation: inflexible sources first,
                    flexible sources adjust to meet remaining demand.</p>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Hydro Profile */}
          <Accordion.Item eventKey="5" id="section-hydro">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">💧 Hydro Generation Profile</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">EMBER | Monthly generation data</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">Residual Load | Calculated from demand minus other sources</span>
                </div>
                <div className="space-y-2">
                  <p><strong>Source:</strong> EMBER Monthly Generation + Residual Load Pattern</p>
                  <div>
                    <p className="font-medium mb-1">How it works:</p>
                    <p className="mb-2">Hydro is the &quot;smart filler&quot; - it responds to the residual load pattern while
                    respecting physical and operational constraints.</p>
                    <p className="font-medium mb-1">Smart Dispatch Method:</p>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>
                        <strong>Calculate Residual Load</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>For each hour: Demand - Nuclear - Solar - Wind</li>
                          <li>This shows when and how much flexible generation is needed</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Shape to Residual Pattern</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Hydro generation follows residual load shape</li>
                          <li>High generation when residual is high (peak demand, low renewables)</li>
                          <li>Low/zero generation when residual is low or negative</li>
                          <li>Creates responsive, realistic dispatch pattern</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Apply Monthly Constraints</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Historical monthly hydro generation from EMBER</li>
                          <li>Total monthly energy preserved (water budget)</li>
                          <li>Pattern shaped, but monthly totals match reality</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Capacity Limits</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Maximum output capped at installed capacity</li>
                          <li>Can&apos;t exceed turbine/generator limits</li>
                          <li>Reflects physical plant constraints</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Scaling with Capacity</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Your capacity / Historical capacity = Scale Factor</li>
                          <li>Monthly generation scaled proportionally</li>
                          <li>Pattern preserved, magnitude adjusted</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Why This Approach:</p>
                    <p>Unlike nuclear (flat) or solar/wind (weather-driven), hydro is flexible.
                    Real hydro operators dispatch based on residual demand. Our methodology replicates
                    this intelligent dispatch while respecting water availability (monthly totals)
                    and physical limits (capacity).</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">What You See:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Pattern follows residual load (high correlation)</li>
                      <li>Zero generation during over-generation periods</li>
                      <li>Higher output during low-renewable, high-demand periods</li>
                      <li>Monthly totals match historical water availability</li>
                      <li>Realistic operational flexibility</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                    <p className="font-medium text-blue-900 mb-1">💡 Why This Is Clever:</p>
                    <div className="text-blue-800 text-xs space-y-2">
                      <p><strong>Traditional approach:</strong> Flat capacity factor or simple demand-following</p>
                      <p><strong>Our approach:</strong></p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Hydro sees the actual &quot;gap&quot; after baseload and VRE</li>
                        <li>Responds to residual load pattern (like real operators)</li>
                        <li>Respects water availability (monthly EMBER data)</li>
                        <li>Validated against expected dispatch behavior</li>
                      </ol>
                      <p className="mt-2"><strong>Result:</strong> Realistic hydro profiles that reflect actual operational flexibility
                      and physical constraints.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Data Sources */}
          <Accordion.Item eventKey="6" id="section-data-sources">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">🌍 Global Data Sources</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <p>This simulator is built on trusted, open datasets used by researchers and policymakers worldwide:</p>
                <div className="space-y-2">
                  <div>
                    <p className="font-medium">📊 EMBER (Electricity Maps & Baseline Energy Research)</p>
                    <p className="ml-6">→ Monthly and annual electricity generation by fuel type</p>
                    <p className="ml-6">→ Used by: IEA, World Bank, academic researchers</p>
                  </div>
                  <div>
                    <p className="font-medium">📊 IRENA (International Renewable Energy Agency)</p>
                    <p className="ml-6">→ Global renewable energy capacity database</p>
                    <p className="ml-6">→ Used by: UN agencies, governments, industry</p>
                  </div>
                  <div>
                    <p className="font-medium">☀️💨 Atlite (Open Energy Modelling Framework)</p>
                    <p className="ml-6">→ Renewable generation profiles from ERA5 climate data</p>
                    <p className="ml-6">→ Used by: Academic energy system studies, policy analysis</p>
                  </div>
                  <div>
                    <p className="font-medium">🌡️ ERA5 (ECMWF Climate Reanalysis)</p>
                    <p className="ml-6">→ Global climate and weather data</p>
                    <p className="ml-6">→ Gold standard for climate analysis worldwide</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="font-medium mb-2">All data sources are:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>✓ Publicly available or openly documented</li>
                    <li>✓ Regularly updated with official statistics</li>
                    <li>✓ Peer-reviewed or institutionally verified</li>
                    <li>✓ Widely used in energy research and policy</li>
                  </ul>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Key Assumptions */}
          <Accordion.Item eventKey="7" id="section-assumptions">
            <Accordion.Header className="px-4 md:px-6 lg:px-8 py-3 md:py-5">
              <span className="text-sm md:text-base font-semibold">⚙️ Key Assumptions</span>
            </Accordion.Header>
            <Accordion.Body className="md:px-6 lg:px-8 md:pb-5 md:pt-1">
              <div className="text-xs lg:text-sm text-gray-700 space-y-3">
                <p>To provide fast, accessible energy modeling, we make several simplifying assumptions:</p>
                <div>
                  <p className="font-medium mb-2">What We Model:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>✓ 8,760 hourly generation profiles</li>
                    <li>✓ Optimal renewable energy siting (best locations)</li>
                    <li>✓ Standard technology performance</li>
                    <li>✓ Climate-driven patterns (weather, demand)</li>
                    <li>✓ Operational constraints (capacity, monthly energy)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">What We Don&apos;t Model:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>✗ Electricity transmission and grid constraints</li>
                    <li>✗ Energy storage systems</li>
                    <li>✗ Sub-hourly variability or ramps</li>
                    <li>✗ Economic dispatch or electricity prices</li>
                    <li>✗ Multi-year reservoir operations</li>
                    <li>✗ Demand response or flexibility</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Best Used For:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Annual energy balance studies</li>
                    <li>Renewable integration analysis</li>
                    <li>Capacity planning scenarios</li>
                    <li>Understanding generation patterns</li>
                    <li>Policy and planning discussions</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Not Suitable For:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Real-time grid operations</li>
                    <li>Transmission planning</li>
                    <li>Economic optimization</li>
                    <li>Sub-hourly balancing</li>
                    <li>Forecasting future years</li>
                  </ul>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        </div>
      </div>
    </div>
  );
});

GenerationProfileMethodologyAccordion.displayName = 'GenerationProfileMethodologyAccordion';

export default GenerationProfileMethodologyAccordion;
